import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Chip,
  Typography,
  Alert,
  FormControlLabel,
  Switch,
  Card,
  CardContent,
  Grid,
  Divider,
  Collapse,
  CircularProgress
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  DragHandle as DragHandleIcon
} from '@mui/icons-material';
import { characteristicsAPI } from '../api/characteristics';
import { dataTypesAPI } from '../api/dataTypes';

// Транслітерація для автогенерації коду
const transliterate = (text) => {
  const map = {
    'а': 'a', 'б': 'b', 'в': 'v', 'г': 'g', 'д': 'd', 'е': 'e', 'ё': 'e',
    'ж': 'zh', 'з': 'z', 'и': 'i', 'й': 'y', 'к': 'k', 'л': 'l', 'м': 'm',
    'н': 'n', 'о': 'o', 'п': 'p', 'р': 'r', 'с': 's', 'т': 't', 'у': 'u',
    'ф': 'f', 'х': 'h', 'ц': 'ts', 'ч': 'ch', 'ш': 'sh', 'щ': 'sch',
    'ъ': '', 'ы': 'y', 'ь': '', 'э': 'e', 'ю': 'yu', 'я': 'ya',
    'і': 'i', 'ї': 'yi', 'є': 'ye', 'ґ': 'g',
    ' ': '_', '-': '_'
  };
  return text.toLowerCase().split('').map(char => map[char] || char).join('')
    .replace(/[^a-z0-9_]/g, '')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
};

function Characteristics() {
  const [characteristics, setCharacteristics] = useState([]);
  const [dataTypes, setDataTypes] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    is_multiple: false,
    is_searchable: false,
    show_in_list: false,
    show_in_summary: false,
    fields: []
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setInitialLoading(true);
      const [chars, types] = await Promise.all([
        characteristicsAPI.getAll(),
        dataTypesAPI.getAll()
      ]);
      setCharacteristics(chars);
      setDataTypes(types);
    } catch (err) {
      setError('Ошибка загрузки: ' + (err.response?.data?.detail || err.message));
    } finally {
      setInitialLoading(false);
    }
  };

  const getDefaultTypeId = () => {
    const stringType = dataTypes.find(t => t.code === 'string');
    return stringType?.id || dataTypes[0]?.id || '';
  };

  const handleOpenDialog = async (item = null) => {
    setError('');

    if (item) {
      try {
        const fullData = await characteristicsAPI.getById(item.id);
        setEditingItem(fullData);
        setFormData({
          name: fullData.name,
          code: fullData.code,
          description: fullData.description || '',
          is_multiple: fullData.is_multiple,
          is_searchable: fullData.is_searchable,
          show_in_list: fullData.show_in_list || false,
          show_in_summary: fullData.show_in_summary || false,
          fields: fullData.fields?.map(f => ({
            ...f,
            enum_values: f.enum_values ? f.enum_values.join(', ') : ''
          })) || []
        });
      } catch (err) {
        setError('Ошибка загрузки: ' + (err.response?.data?.detail || err.message));
        return;
      }
    } else {
      setEditingItem(null);
      const defaultTypeId = getDefaultTypeId();
      setFormData({
        name: '',
        code: '',
        description: '',
        is_multiple: false,
        is_searchable: false,
        show_in_list: false,
        show_in_summary: false,
        fields: [{
          name: 'Значение',
          code: 'value',
          data_type_id: defaultTypeId,
          is_required: false,
          enum_values: '',
          placeholder: '',
          order: 0
        }]
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingItem(null);
    setError('');
  };

  // Автогенерація коду з назви
  const handleNameChange = (e) => {
    const name = e.target.value;
    const newData = { ...formData, name };

    // Автогенерація коду тільки для нових записів
    if (!editingItem) {
      newData.code = transliterate(name);
    }

    setFormData(newData);
  };

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  // Автогенерація коду поля з назви
  const handleFieldNameChange = (index, name) => {
    const newFields = [...formData.fields];
    newFields[index] = {
      ...newFields[index],
      name,
      code: !editingItem ? transliterate(name) : newFields[index].code
    };
    setFormData({ ...formData, fields: newFields });
  };

  const handleFieldChange = (index, field, value) => {
    const newFields = [...formData.fields];
    newFields[index] = { ...newFields[index], [field]: value };
    setFormData({ ...formData, fields: newFields });
  };

  const addField = () => {
    const defaultTypeId = getDefaultTypeId();
    setFormData({
      ...formData,
      fields: [...formData.fields, {
        name: '',
        code: '',
        data_type_id: defaultTypeId,
        is_required: false,
        enum_values: '',
        placeholder: '',
        order: formData.fields.length
      }]
    });
  };

  const removeField = (index) => {
    const newFields = formData.fields.filter((_, i) => i !== index);
    newFields.forEach((f, i) => f.order = i);
    setFormData({ ...formData, fields: newFields });
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    try {
      // Валідація
      if (!formData.name.trim()) {
        throw new Error('Введите название характеристики');
      }

      if (!formData.fields || formData.fields.length === 0) {
        throw new Error('Добавьте хотя бы одно поле');
      }

      for (const field of formData.fields) {
        if (!field.name.trim()) {
          throw new Error('Заполните названия всех полей');
        }
        if (!field.data_type_id) {
          throw new Error('Выберите тип для всех полей');
        }
      }

      const payload = {
        name: formData.name.trim(),
        code: formData.code || transliterate(formData.name),
        description: formData.description || null,
        icon: null,
        is_multiple: formData.is_multiple,
        min_count: 0,
        max_count: null,
        is_searchable: formData.is_searchable,
        show_in_list: formData.show_in_list,
        show_in_summary: formData.show_in_summary,
        fields: formData.fields.map((f, idx) => ({
          name: f.name.trim(),
          code: f.code || transliterate(f.name),
          data_type_id: parseInt(f.data_type_id),
          is_required: f.is_required || false,
          enum_values: f.enum_values
            ? f.enum_values.split(',').map(v => v.trim()).filter(v => v)
            : null,
          default_value: f.default_value || null,
          placeholder: f.placeholder || null,
          order: idx
        }))
      };

      if (editingItem) {
        const { fields, ...updateData } = payload;
        await characteristicsAPI.update(editingItem.id, updateData);
      } else {
        await characteristicsAPI.create(payload);
      }

      await loadData();
      handleCloseDialog();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Удалить характеристику? Это удалит все связанные данные!')) {
      return;
    }

    try {
      await characteristicsAPI.delete(id);
      await loadData();
    } catch (err) {
      setError('Ошибка удаления: ' + (err.response?.data?.detail || err.message));
    }
  };

  const toggleRowExpand = async (id) => {
    if (!expandedRows[id]) {
      try {
        const fullData = await characteristicsAPI.getById(id);
        setCharacteristics(prev => prev.map(c =>
          c.id === id ? { ...c, fields: fullData.fields } : c
        ));
      } catch (err) {
        console.error('Error loading fields:', err);
      }
    }
    setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const getDataTypeName = (dataTypeId) => {
    const type = dataTypes.find(t => t.id === dataTypeId);
    return type ? type.name : '?';
  };

  const isEnumType = (dataTypeId) => {
    const type = dataTypes.find(t => t.id === dataTypeId);
    return type?.code === 'enum';
  };

  if (initialLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4">Характеристики</Typography>
          <Typography variant="body2" color="textSecondary">
            Шаблоны данных для устройств. Каждая характеристика может иметь одно или несколько полей.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          disabled={dataTypes.length === 0}
        >
          Добавить
        </Button>
      </Box>

      {dataTypes.length === 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Нет базовых типов данных! Обратитесь к администратору.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width={40}></TableCell>
              <TableCell>Название</TableCell>
              <TableCell align="center">Полей</TableCell>
              <TableCell align="center">Множественная</TableCell>
              <TableCell align="center">Поиск</TableCell>
              <TableCell>Описание</TableCell>
              <TableCell align="right">Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {characteristics.map((item) => (
              <React.Fragment key={item.id}>
                <TableRow hover>
                  <TableCell>
                    <IconButton size="small" onClick={() => toggleRowExpand(item.id)}>
                      {expandedRows[item.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight="medium">{item.name}</Typography>
                    <Typography variant="caption" color="textSecondary">{item.code}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={item.field_count || 0} size="small" color="primary" variant="outlined" />
                  </TableCell>
                  <TableCell align="center">
                    {item.is_multiple ? (
                      <Chip label="Да" color="success" size="small" />
                    ) : (
                      <Typography color="textSecondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {item.is_searchable ? (
                      <Chip label="Так" color="info" size="small" />
                    ) : (
                      <Typography color="textSecondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textSecondary" noWrap sx={{ maxWidth: 200 }}>
                      {item.description || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleOpenDialog(item)} color="primary">
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(item.id)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
                {expandedRows[item.id] && (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ py: 0, bgcolor: 'grey.50' }}>
                      <Collapse in={expandedRows[item.id]} timeout="auto" unmountOnExit>
                        <Box sx={{ m: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>Поля:</Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {item.fields?.map((field, idx) => (
                              <Chip
                                key={idx}
                                label={`${field.name} (${field.data_type_name || getDataTypeName(field.data_type_id)})`}
                                size="small"
                                color={field.is_required ? 'primary' : 'default'}
                                variant={field.is_required ? 'filled' : 'outlined'}
                              />
                            )) || <CircularProgress size={20} />}
                          </Box>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
            {characteristics.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="textSecondary" sx={{ py: 3 }}>
                    Характеристик нет. Создайте первую!
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Діалог створення/редагування */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingItem ? 'Редактировать характеристику' : 'Новая характеристика'}
        </DialogTitle>
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Название характеристики"
            value={formData.name}
            onChange={handleNameChange}
            required
            margin="normal"
            placeholder="Например: Сетевая карта"
            autoFocus
          />

          <TextField
            fullWidth
            label="Описание (необязательно)"
            name="description"
            value={formData.description}
            onChange={handleChange}
            margin="normal"
            multiline
            rows={2}
            placeholder="Краткое описание для чего используется"
          />

          <Box sx={{ mt: 2, display: 'flex', gap: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_multiple}
                  onChange={handleChange}
                  name="is_multiple"
                />
              }
              label="Можно добавлять несколько"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_searchable}
                  onChange={handleChange}
                  name="is_searchable"
                />
              }
              label="Поиск по этому полю"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.show_in_list}
                  onChange={handleChange}
                  name="show_in_list"
                />
              }
              label="Показывать в списке устройств"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.show_in_summary}
                  onChange={handleChange}
                  name="show_in_summary"
                />
              }
              label="Включить в краткий отчет"
            />
          </Box>

          <Divider sx={{ my: 3 }}>
            <Chip label="Поля характеристики" />
          </Divider>

          {formData.fields.map((field, index) => (
            <Card key={index} variant="outlined" sx={{ mb: 2 }}>
              <CardContent sx={{ pb: '8px !important' }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item>
                    <DragHandleIcon color="disabled" />
                  </Grid>
                  <Grid item xs>
                    <TextField
                      fullWidth
                      size="small"
                      label="Название поля"
                      value={field.name}
                      onChange={(e) => handleFieldNameChange(index, e.target.value)}
                      required
                      placeholder="Например: IP адрес"
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      fullWidth
                      size="small"
                      select
                      label="Тип"
                      value={field.data_type_id || ''}
                      onChange={(e) => handleFieldChange(index, 'data_type_id', e.target.value)}
                      required
                    >
                      {dataTypes.map((type) => (
                        <MenuItem key={type.id} value={type.id}>
                          {type.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={field.is_required || false}
                          onChange={(e) => handleFieldChange(index, 'is_required', e.target.checked)}
                        />
                      }
                      label="Обязательное"
                    />
                  </Grid>
                  <Grid item>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => removeField(index)}
                      disabled={formData.fields.length <= 1}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>

                {isEnumType(field.data_type_id) && (
                  <TextField
                    fullWidth
                    size="small"
                    label="Варианты выбора (через запятую)"
                    value={field.enum_values || ''}
                    onChange={(e) => handleFieldChange(index, 'enum_values', e.target.value)}
                    sx={{ mt: 2 }}
                    placeholder="Активный, Неактивный, В ремонте"
                  />
                )}

                <TextField
                  fullWidth
                  size="small"
                  label="Подсказка (placeholder)"
                  value={field.placeholder || ''}
                  onChange={(e) => handleFieldChange(index, 'placeholder', e.target.value)}
                  sx={{ mt: 1 }}
                  placeholder="Текст-подсказка в поле ввода"
                />
              </CardContent>
            </Card>
          ))}

          {!editingItem && (
            <Button
              startIcon={<AddIcon />}
              onClick={addField}
              variant="outlined"
              size="small"
            >
              Добавить поле
            </Button>
          )}

          {editingItem && (
            <Alert severity="info" sx={{ mt: 2 }}>
              При редактировании можно изменить только основные настройки. Поля редактируются отдельно.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Отмена</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || !formData.name.trim()}
          >
            {loading ? 'Сохранение...' : 'Сохранить'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Characteristics;
